import React from 'react';
import { PortfolioItem } from '@/hooks/useProfile';
import ImageCarousel from './ImageCarousel';
import ImageCaption from './ImageCaption';
import Thumbnails from './Thumbnails';
import NoImagesFallback from './NoImagesFallback';
import { usePortfolioModalState } from './usePortfolioModalState';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { RichTextDisplay } from '@/components/ui/rich-text-display';

interface PortfolioModalContentProps {
  item: PortfolioItem;
  onClose: () => void;
  username: string;
  username_slug: string;
}

const PortfolioModalContent = ({ 
  item, 
  onClose, 
  username,
  username_slug
}: PortfolioModalContentProps) => {
  const {
    images,
    currentIndex,
    setCurrentIndex,
    imageError,
    handleImageError,
    currentImage
  } = usePortfolioModalState(item);
  
  const isMobile = useIsMobile();

  return (
    <div className={`bg-white rounded-lg flex flex-col ${!isMobile ? 'md:flex-row' : ''} overflow-hidden w-full h-[85vh]`}>
      {/* Left side: Image section */}
      <div className={`w-full ${!isMobile ? 'md:w-3/4' : ''} p-3 md:p-6`}>
        {/* Main Image Carousel */}
        {images.length > 0 ? (
          <div className="space-y-3 md:space-y-5">
            <div className={`flex ${isMobile ? 'flex-col' : 'gap-4'}`}>
              {/* Thumbnails - Below on mobile, left on desktop */}
              {images.length > 1 && !isMobile && (
                <ScrollArea className="">
                  <div className="flex flex-col gap-2">
                    <Thumbnails
                      images={images}
                      currentIndex={currentIndex}
                      setCurrentIndex={setCurrentIndex}
                      imageError={imageError}
                      handleImageError={handleImageError}
                      vertical={true}
                    />
                  </div>
                </ScrollArea>
              )}

              {/* Main Image */}
              <div className="flex-1 rounded-lg overflow-hidden shadow-md">
                <ImageCarousel
                  images={images}
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                  handleImageError={handleImageError}
                  imageError={imageError}
                  itemTitle={item.title}
                />
              </div>

              {/* Thumbnails for mobile only */}
              {images.length > 1 && isMobile && (
                <ScrollArea className="w-full mt-4">
                  <div className="flex flex-row gap-2">
                    <Thumbnails
                      images={images}
                      currentIndex={currentIndex}
                      setCurrentIndex={setCurrentIndex}
                      imageError={imageError}
                      handleImageError={handleImageError}
                      vertical={false}
                    />
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Image Caption */}
            <ImageCaption currentImage={currentImage} />
          </div>
        ) : (
          <NoImagesFallback />
        )}
      </div>
      
      {/* Right side: Description section - Fixed scroll area for mobile */}
      <div className={`w-full ${!isMobile ? 'md:w-1/4' : ''} p-3 md:p-4 ${isMobile ? '' : 'md:border-l'} ${isMobile ? 'flex-1 min-h-0' : ''} flex flex-col`}>
        <ScrollArea className={`${isMobile ? 'h-[50vh]' : 'h-[calc(80vh-150px)]'} md:h-[70vh] w-full overflow-y-auto pb-4 flex-1`}>
          <div className="pr-4">
            <div className="flex justify-between items-start my-4">
              <div>
                <h2 className="text-xl font-bold">{item.title}</h2>
                {/* Add project metadata */}
                {item.created_at && (
                  <div className="text-xs text-muted-foreground">
                    <p>Created: {new Date(item.created_at).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="prose prose-sm max-w-none">
              <RichTextDisplay 
                content={item.description || 'No description provided.'} 
              />
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default PortfolioModalContent;
